export function RadarChart({ dimensions }: { dimensions: { dimension: string; score: number; maxScore: number }[] }) {
  const size = 400;
  const center = size / 2;
  const maxRadius = 160;
  const levels = 5;
  const angleStep = (Math.PI * 2) / dimensions.length;

  // Calculate points for each level (concentric polygons)
  const getLevelPoints = (level: number) => {
    const radius = (maxRadius / levels) * level;
    return dimensions.map((_, i) => {
      const angle = angleStep * i - Math.PI / 2;
      return {
        x: center + radius * Math.cos(angle),
        y: center + radius * Math.sin(angle)
      };
    });
  };

  // Calculate data points
  const dataPoints = dimensions.map((dim, i) => {
    const radius = dim.maxScore > 0 ? (maxRadius * dim.score) / dim.maxScore : 0;
    const angle = angleStep * i - Math.PI / 2;
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle)
    };
  });

  // Convert points array to SVG path
  const pointsToPath = (points: { x: number; y: number }[]) => {
    if (points.length === 0) return '';
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
  };

  // Calculate label positions (outside the chart)
  const labelRadius = maxRadius + 50;
  const labels = dimensions.map((dim, i) => {
    const angle = angleStep * i - Math.PI / 2;
    const x = center + labelRadius * Math.cos(angle);
    const y = center + labelRadius * Math.sin(angle);
    
    // Determine text anchor based on position
    let textAnchor = 'middle';
    if (x < center - 10) textAnchor = 'end';
    else if (x > center + 10) textAnchor = 'start';
    
    return { x, y, text: dim.dimension, textAnchor, score: dim.score };
  });

  return (
    <svg width={size} height={size} className="mx-auto">
      {/* Background circles */}
      {[...Array(levels)].map((_, i) => (
        <circle
          key={`circle-${i}`}
          cx={center}
          cy={center}
          r={(maxRadius / levels) * (i + 1)}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="1"
        />
      ))}

      {/* Grid lines from center to each axis */}
      {dimensions.map((_, i) => {
        const angle = angleStep * i - Math.PI / 2;
        const x = center + maxRadius * Math.cos(angle);
        const y = center + maxRadius * Math.sin(angle);
        return (
          <line
            key={`line-${i}`}
            x1={center}
            y1={center}
            x2={x}
            y2={y}
            stroke="#e5e7eb"
            strokeWidth="1"
          />
        );
      })}

      {/* Level polygons (gridlines) */}
      {[...Array(levels)].map((_, i) => (
        <path
          key={`level-${i}`}
          d={pointsToPath(getLevelPoints(i + 1))}
          fill="none"
          stroke="#f3f4f6"
          strokeWidth="1"
        />
      ))}

      {/* Data polygon (filled area) */}
      <path
        d={pointsToPath(dataPoints)}
        fill="rgba(59, 130, 246, 0.2)"
        stroke="rgb(59, 130, 246)"
        strokeWidth="2"
      />

      {/* Data points (dots) */}
      {dataPoints.map((point, i) => (
        <circle
          key={`dot-${i}`}
          cx={point.x}
          cy={point.y}
          r="4"
          fill="rgb(59, 130, 246)"
        />
      ))}

      {/* Labels */}
      {labels.map((label, i) => (
        <g key={`label-${i}`}>
          <text
            x={label.x}
            y={label.y}
            textAnchor={label.textAnchor as 'start' | 'middle' | 'end'}
            fill="#4b5563"
            fontSize="13"
            fontWeight="500"
            dominantBaseline="middle"
          >
            {label.text}
          </text>
          <text
            x={label.x}
            y={label.y + 16}
            textAnchor={label.textAnchor as 'start' | 'middle' | 'end'}
            fill="#6b7280"
            fontSize="11"
            dominantBaseline="middle"
          >
            {label.score}
          </text>
        </g>
      ))}

      {/* Center point */}
      <circle cx={center} cy={center} r="3" fill="#6b7280" />

      {/* Legend */}
      <g transform={`translate(${center - 40}, ${size - 30})`}>
        <rect x="0" y="0" width="12" height="12" fill="rgba(59, 130, 246, 0.5)" stroke="rgb(59, 130, 246)" strokeWidth="1" rx="2" />
        <text x="18" y="10" fill="#6b7280" fontSize="11">Score</text>
      </g>
    </svg>
  );
}
